import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DataObject as JsonIcon,
} from "@mui/icons-material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";
import { useDispatch } from "react-redux";
import { openEmailModal, openShipmentModal } from "../../store/adminSlice";
import { MonacoJsonEditor } from "../../components/MonacoJsonEditor";
import { InputItemDisplay } from "../emailAutomationPipeline/components/InputItemDisplay";
import type { InputItem } from "../emailAutomationPipeline/components/types";

interface EmailInfo {
  id: string;
  subject: string | null;
  fromEmail: string | null;
  date: string;
}

interface EmailTestSetDetail {
  id: string;
  name: string;
  description: string | null;
  emailIds: string[];
  inputs: InputItem[];
  emails: EmailInfo[];
  createdAt: string;
  updatedAt: string;
}

export function EmailTestSetDetailPage() {
  const { testSetId } = useParams<{ testSetId: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [testSet, setTestSet] = useState<EmailTestSetDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add emails dialog
  const [addEmailsDialogOpen, setAddEmailsDialogOpen] = useState(false);
  const [emailIdsToAdd, setEmailIdsToAdd] = useState("");
  const [adding, setAdding] = useState(false);

  // Add input dialog
  const [addInputDialogOpen, setAddInputDialogOpen] = useState(false);
  const [inputJson, setInputJson] = useState("");
  const [addingInput, setAddingInput] = useState(false);

  useEffect(() => {
    if (testSetId) {
      fetchTestSet();
    }
  }, [testSetId]);

  const fetchTestSet = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ testSet: EmailTestSetDetail }>(
        `/admin/email-test-sets/get`,
        { id: testSetId }
      );
      setTestSet(res.testSet);
    } catch (error) {
      console.error("Error fetching test set:", error);
      setError("Failed to load test set");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmails = async () => {
    if (!testSetId) return;

    const trimmed = emailIdsToAdd.trim();
    if (!trimmed) {
      showSnackbar("Please enter at least one email ID or JSON input", 3000, "info");
      return;
    }

    // Detect JSON input (object or array)
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      let raw: unknown;
      try {
        raw = JSON.parse(trimmed);
      } catch {
        showSnackbar("Invalid JSON", 3000, "error");
        return;
      }

      const items: Record<string, unknown>[] = Array.isArray(raw) ? raw : [raw];
      for (const item of items) {
        const hasValidKey =
          typeof item.emailId === "string" ||
          typeof item.shipmentId === "string" ||
          typeof item.reminderId === "string";
        if (!hasValidKey) {
          showSnackbar(
            "Each input must have at least one of: emailId, shipmentId, reminderId",
            4000,
            "error"
          );
          return;
        }
      }

      setAdding(true);
      try {
        for (const item of items) {
          await apiClient.post(`/admin/email-test-sets/add-input`, {
            testSetId,
            input: item,
          });
        }
        showSnackbar(`${items.length} input(s) added`, 3000, "success");
        setAddEmailsDialogOpen(false);
        setEmailIdsToAdd("");
        fetchTestSet();
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error || "Failed to add input";
        showSnackbar(errorMessage, 4000, "error");
      } finally {
        setAdding(false);
      }
      return;
    }

    // Plain email IDs (comma or newline separated)
    const emailIds = trimmed
      .split(/[,\s]+/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (emailIds.length === 0) {
      showSnackbar("Please enter at least one email ID", 3000, "info");
      return;
    }

    setAdding(true);
    try {
      const res = await apiClient.post<{
        success: boolean;
        added: number;
        alreadyInSet: number;
        notFound: string[];
      }>(`/admin/email-test-sets/add-emails`, {
        testSetId,
        emailIds,
      });

      let message = `Added ${res.added} email(s)`;
      if (res.alreadyInSet > 0) {
        message += `, ${res.alreadyInSet} already in set`;
      }
      if (res.notFound.length > 0) {
        message += `, ${res.notFound.length} not found`;
      }

      showSnackbar(message, 4000, "success");
      setAddEmailsDialogOpen(false);
      setEmailIdsToAdd("");
      fetchTestSet();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to add emails";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setAdding(false);
    }
  };

  const handleAddInput = async () => {
    if (!testSetId) return;

    let raw: unknown;
    try {
      raw = JSON.parse(inputJson);
    } catch {
      showSnackbar("Invalid JSON", 3000, "error");
      return;
    }

    const items: Record<string, unknown>[] = Array.isArray(raw) ? raw : [raw];

    for (const item of items) {
      const hasValidKey =
        typeof item.emailId === "string" ||
        typeof item.shipmentId === "string" ||
        typeof item.reminderId === "string";
      if (!hasValidKey) {
        showSnackbar(
          "Each input must have at least one of: emailId, shipmentId, reminderId",
          4000,
          "error"
        );
        return;
      }
    }

    setAddingInput(true);
    try {
      for (const item of items) {
        await apiClient.post(`/admin/email-test-sets/add-input`, {
          testSetId,
          input: item,
        });
      }
      showSnackbar(`${items.length} input(s) added`, 3000, "success");
      setAddInputDialogOpen(false);
      setInputJson("");
      fetchTestSet();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to add input";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setAddingInput(false);
    }
  };

  const handleRemoveEmail = async (emailId: string) => {
    if (!testSet) return;

    try {
      await apiClient.post(`/admin/email-test-sets/remove-email`, {
        testSetId: testSet.id,
        emailId,
      });
      showSnackbar("Email removed from test set", 2000, "success");
      fetchTestSet();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to remove email";
      showSnackbar(errorMessage, 3000, "error");
    }
  };

  const handleRemoveInput = async (index: number) => {
    if (!testSet) return;

    try {
      await apiClient.post(`/admin/email-test-sets/remove-input`, {
        testSetId: testSet.id,
        index,
      });
      showSnackbar("Input removed from test set", 2000, "success");
      fetchTestSet();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to remove input";
      showSnackbar(errorMessage, 3000, "error");
    }
  };

  const handleOpenEmail = (emailId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openEmailModal(emailId));
  };

  const handleOpenShipment = (shipmentId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openShipmentModal(shipmentId));
  };

  // Build a map from emailId -> EmailInfo for quick lookups
  const emailMap = new Map<string, EmailInfo>();
  if (testSet) {
    for (const email of testSet.emails) {
      emailMap.set(email.id, email);
    }
  }

  if (loading) {
    return (
      <AdminPage title="Test Set Details">
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  if (error || !testSet) {
    return (
      <AdminPage title="Test Set Details">
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "Test set not found"}
          </Alert>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/admin/test-sets")}
          >
            Back to Test Sets
          </Button>
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage title={testSet.name}>
      <Box sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate("/admin/test-sets")}
              sx={{ mb: 1 }}
            >
              Back to Test Sets
            </Button>
            <Typography variant="h5">{testSet.name}</Typography>
            {testSet.description && (
              <Typography variant="body2" color="text.secondary">
                {testSet.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddEmailsDialogOpen(true)}
            >
              Add Emails
            </Button>
            <Button
              variant="outlined"
              startIcon={<JsonIcon />}
              onClick={() => setAddInputDialogOpen(true)}
            >
              Add Input
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2}>
          {testSet.inputs.length} input(s) in this test set
        </Typography>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Object(s)</TableCell>
                <TableCell>Details</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testSet.inputs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No inputs in this test set. Click "Add Emails" or "Add
                      Input" to add some.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                testSet.inputs.map((input, index) => {
                  const email = input.emailId
                    ? emailMap.get(input.emailId)
                    : undefined;
                  const isEmailInput = !!input.emailId;

                  return (
                    <TableRow key={index} hover>
                      <TableCell>
                        <InputItemDisplay
                          input={input}
                          onOpenEmail={handleOpenEmail}
                          onOpenShipment={handleOpenShipment}
                        />
                      </TableCell>
                      <TableCell>
                        {email && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", mb: 0.5 }}>
                            <Typography variant="body2" component="span" sx={{ maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mr: 1 }}>
                              <strong>Subject:</strong> {email.subject || "(No subject)"}
                            </Typography>
                            <Typography variant="body2" component="span" color="text.secondary" sx={{ mr: 1 }}>
                              <strong>From:</strong> {email.fromEmail || "Unknown"}
                            </Typography>
                            <Typography variant="body2" component="span" color="text.secondary">
                              <strong>Date:</strong> {new Date(email.date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1,
                            fontSize: "0.75rem",
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            overflow: "auto",
                            maxHeight: 200,
                            maxWidth: 500,
                          }}
                        >
                          {JSON.stringify(input, null, 2)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove from test set">
                          <IconButton
                            size="small"
                            onClick={() =>
                              isEmailInput && input.emailId
                                ? handleRemoveEmail(input.emailId)
                                : handleRemoveInput(index)
                            }
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Emails Dialog */}
        <Dialog
          open={addEmailsDialogOpen}
          onClose={() => !adding && setAddEmailsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Emails to Test Set</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter email IDs separated by commas or newlines, or paste JSON
              objects/arrays with emailId, shipmentId, reminderId, cutOffDate, etc.
            </Typography>
            <TextField
              fullWidth
              label="Email IDs or JSON"
              value={emailIdsToAdd}
              onChange={(e) => setEmailIdsToAdd(e.target.value)}
              multiline
              rows={4}
              placeholder={'email-id-1, email-id-2\nor\n[{"shipmentId": "...", "cutOffDate": "2024-06-01"}]'}
              autoFocus
              disabled={adding}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setAddEmailsDialogOpen(false)}
              disabled={adding}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddEmails}
              disabled={!emailIdsToAdd.trim() || adding}
            >
              {adding ? <CircularProgress size={20} /> : "Add Emails"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Input Dialog */}
        <Dialog
          open={addInputDialogOpen}
          onClose={() => !addingInput && setAddInputDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Input to Test Set</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter a JSON object or an array of objects. Each must have at
              least one of: emailId, shipmentId, reminderId.
            </Typography>
            <MonacoJsonEditor
              value={inputJson || '[{\n  \n}]'}
              onChange={(val) => setInputJson(val)}
              height={150}
              readOnly={addingInput}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setAddInputDialogOpen(false)}
              disabled={addingInput}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddInput}
              disabled={!inputJson.trim() || addingInput}
            >
              {addingInput ? <CircularProgress size={20} /> : "Add Input"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
}
